import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { FaUserFriends } from 'react-icons/fa';

import { 
    COLORS, 
    PanelContent, 
    PanelTitle,
    StatGrid,
    StatCard,
    TopicTag
} from './styles';

// Mock data for Client Insights
const mockClientInsightsData = {
    demographics: {
        age: [
            { name: '18-24', value: 10 },
            { name: '25-34', value: 35 },
            { name: '35-44', value: 30 },
            { name: '45-54', value: 15 },
            { name: '55+', value: 10 }
        ],
        location: [
            { name: 'North America', value: 45 },
            { name: 'Europe', value: 30 },
            { name: 'Asia', value: 15 },
            { name: 'Other', value: 10 }
        ]
    },
    frequentTopics: [
        "Career transition strategies",
        "Team management issues",
        "Work-life balance",
        "Salary negotiation"
    ],
    satisfactionScore: 4.7,
    returningClientsPercentage: 65
};

/**
 * ClientInsights Component
 * 
 * Displays client demographic and satisfaction data
 */
const ClientInsights = () => {
    const [chartRadius, setChartRadius] = useState(35);
    const [circleSize, setCircleSize] = useState(65);
    const [textSize, setTextSize] = useState(18);
    
    useEffect(() => {
        const updateChartDimensions = () => {
            if (window.innerWidth <= 1366) {
                setChartRadius(28); // Increased from 22
                setCircleSize(45);
                setTextSize(12);
            } else if (window.innerWidth <= 1920) {
                setChartRadius(35); // Increased from 28
                setCircleSize(50);
                setTextSize(14);
            } else {
                setChartRadius(48); // Increased from 40
                setCircleSize(65);
                setTextSize(20);
            }
        };
        
        updateChartDimensions();
        window.addEventListener('resize', updateChartDimensions);
        
        return () => {
            window.removeEventListener('resize', updateChartDimensions);
        };
    }, []);
    
    return (
        <PanelContent>
            <PanelTitle><FaUserFriends /> Client Insights</PanelTitle>
            
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: window.innerWidth <= 1920 ? '6px' : '10px',
                marginTop: window.innerWidth <= 1920 ? '15px' : '10px' 
            }}>
                <div style={{ width: '48%' }}>
                    <StatCard>
                        <div className="stat-title">Satisfaction Score</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 1920 ? '5px' : '8px' }}>
                            <div className="stat-value">{mockClientInsightsData.satisfactionScore}</div>
                            <div style={{ width: `${circleSize}px`, height: `${circleSize}px` }}>
                                <CircularProgressbar
                                    value={mockClientInsightsData.satisfactionScore * 20}
                                    text={`${mockClientInsightsData.satisfactionScore}/5`}
                                    styles={buildStyles({
                                        textSize: `${textSize}px`,
                                        pathColor: COLORS.success,
                                        textColor: COLORS.light,
                                        trailColor: COLORS.border,
                                        strokeWidth: window.innerWidth <= 1920 ? 8 : 10,
                                    })}
                                />
                            </div>
                        </div>
                    </StatCard>
                </div>
                
                <div style={{ width: '48%' }}>
                    <StatCard>
                        <div className="stat-title">Returning Clients</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 1920 ? '5px' : '8px' }}>
                            <div className="stat-value">{mockClientInsightsData.returningClientsPercentage}%</div>
                            <div style={{ width: `${circleSize}px`, height: `${circleSize}px` }}>
                                <CircularProgressbar
                                    value={mockClientInsightsData.returningClientsPercentage}
                                    text={`${mockClientInsightsData.returningClientsPercentage}%`}
                                    styles={buildStyles({
                                        textSize: `${textSize}px`,
                                        pathColor: COLORS.primary,
                                        textColor: COLORS.light,
                                        trailColor: COLORS.border,
                                        strokeWidth: window.innerWidth <= 1920 ? 8 : 10,
                                    })}
                                />
                            </div>
                        </div>
                    </StatCard>
                </div>
            </div>
            
            <div style={{ 
                marginBottom: window.innerWidth <= 1920 ? '8px' : '12px',
                marginTop: window.innerWidth <= 1920 ? '20px' : '1px'
            }}>
                <div style={{ color: COLORS.light, marginBottom: window.innerWidth <= 1920 ? '3px' : '6px', fontSize: window.innerWidth <= 1920 ? '10px' : '12px' }}>Client Demographics</div>
                <div style={{ display: 'flex', gap: window.innerWidth <= 1920 ? '6px' : '10px' }}>
                    <div style={{ width: '50%', position: 'relative' }}>
                        <div style={{ textAlign: 'center', marginBottom: window.innerWidth <= 1920 ? '1px' : '3px', fontSize: window.innerWidth <= 1920 ? '8px' : '10px', color: COLORS.light }}>Age Distribution</div>
                        <div style={{ height: window.innerWidth <= 1920 ? '110px' : '135px', position: 'relative' }}> {/* Increased from 90px/110px */}
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={mockClientInsightsData.demographics.age}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={chartRadius}
                                        innerRadius={0}
                                        fill="#8884d8"
                                        dataKey="value"
                                        isAnimationActive={false}
                                    >
                                        {mockClientInsightsData.demographics.age.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS.chart[index % COLORS.chart.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip 
                                        formatter={(value, name) => [`${value}%`, name]} 
                                        labelFormatter={() => 'Age Group'}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ 
                            position: 'absolute', 
                            bottom: '-10px', /* Moved from -5px to -10px to provide more space */
                            left: 0, 
                            right: 0, 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: window.innerWidth <= 1920 ? '5px' : '8px',
                            flexWrap: 'wrap'
                        }}>
                            {mockClientInsightsData.demographics.age.map((item, index) => (
                                <div key={index} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '3px',
                                    fontSize: window.innerWidth <= 1920 ? '8px' : '10px', /* Increased from 7px/9px */
                                    color: COLORS.light
                                }}>
                                    <div style={{ 
                                        width: window.innerWidth <= 1920 ? '8px' : '10px', /* Increased from 6px/8px */
                                        height: window.innerWidth <= 1920 ? '8px' : '10px', /* Increased from 6px/8px */
                                        backgroundColor: COLORS.chart[index % COLORS.chart.length],
                                        borderRadius: '50%' 
                                    }}></div>
                                    <span>{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div style={{ width: '50%', position: 'relative' }}>
                        <div style={{ textAlign: 'center', marginBottom: window.innerWidth <= 1920 ? '1px' : '3px', fontSize: window.innerWidth <= 1920 ? '8px' : '10px', color: COLORS.light }}>Geographic Distribution</div>
                        <div style={{ height: window.innerWidth <= 1920 ? '110px' : '135px', position: 'relative' }}> {/* Increased from 90px/110px */}
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={mockClientInsightsData.demographics.location}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={chartRadius}
                                        innerRadius={0}
                                        fill="#8884d8"
                                        dataKey="value"
                                        isAnimationActive={false}
                                    >
                                        {mockClientInsightsData.demographics.location.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS.chart[(index + 3) % COLORS.chart.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip 
                                        formatter={(value, name) => [`${value}%`, name]} 
                                        labelFormatter={() => 'Region'}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ 
                            position: 'absolute', 
                            bottom: '-10px', /* Moved from -5px to -10px to provide more space */
                            left: 0, 
                            right: 0, 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: window.innerWidth <= 1920 ? '5px' : '8px',
                            flexWrap: 'wrap'
                        }}>
                            {mockClientInsightsData.demographics.location.map((item, index) => (
                                <div key={index} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '3px',
                                    fontSize: window.innerWidth <= 1920 ? '8px' : '10px', /* Increased from 7px/9px */
                                    color: COLORS.light
                                }}>
                                    <div style={{ 
                                        width: window.innerWidth <= 1920 ? '8px' : '10px', /* Increased from 6px/8px */
                                        height: window.innerWidth <= 1920 ? '8px' : '10px', /* Increased from 6px/8px */
                                        backgroundColor: COLORS.chart[(index + 3) % COLORS.chart.length],
                                        borderRadius: '50%' 
                                    }}></div>
                                    <span>{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Frequent Topics section with adjusted margin to move it up a bit */}
            <div style={{ 
                marginTop: window.innerWidth <= 1920 ? '25px' : '40px', /* Reduced from 40px/60px to move it up */
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                justifyContent: 'flex-end', /* Added to push content to bottom */
                paddingBottom: window.innerWidth <= 1920 ? '10px' : '15px' /* Added padding at bottom */
            }}>
                <div style={{ 
                    color: COLORS.light, 
                    marginBottom: window.innerWidth <= 1920 ? '6px' : '8px', 
                    fontSize: window.innerWidth <= 1920 ? '11px' : '13px' /* Slightly increased font size */
                }}>Frequent Topics</div>
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: window.innerWidth <= 1920 ? '6px' : '8px'  /* Increased from 4px/6px for more spacing */
                }}>
                    {mockClientInsightsData.frequentTopics.map((topic, index) => (
                        <TopicTag key={index}>{topic}</TopicTag>
                    ))}
                </div>
            </div>
        </PanelContent>
    );
};

export default ClientInsights;
